import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';

import ImagePreview from '@/components/forms/imagePreview';
import { Upload } from '@/components/upload';
import { useDynamicLocale } from '@/hoslog-intl';
import { isGoogleMapsUrl } from '@/lib/isGoogleMapsUrl';
import { optionalUploadValueSchema } from '@/lib/uploadSchema';
import { FileObject, uploadFileToS3WithPresignedUrl } from '@/lib/uploadToAWS';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import { useLocationDetail } from './hooks/useLocationDetail';
import useUpdateLocationDetail from './hooks/useUpdateLocationDetail';

const formSchema = z
  .object({
    title: z.string().trim().min(1, { message: 'common.form.validation.required' }),
    subtitle: z.string().trim().min(1, {
      message: 'common.form.validation.required',
    }),
    existingImageSrc: z.string().optional().default(''),
    image: optionalUploadValueSchema,
    mapLink: z
      .string()
      .trim()
      .min(1, 'common.form.validation.required')
      .url('Please enter a valid URL.')
      .refine(isGoogleMapsUrl, {
        message: 'common.form.validation.enterValidUrl',
      }),
    latitude: z
      .string()
      .trim()
      .min(1, { message: 'common.form.validation.required' })
      .regex(/^-?\d+(\.\d+)?$/, { message: 'common.form.validation.invalid' })
      .refine(
        (val) => {
          const num = parseFloat(val);
          return num >= -90 && num <= 90;
        },
        { message: 'common.form.validation.latitudeMessage' },
      ),
    longitude: z
      .string()
      .trim()
      .min(1, { message: 'common.form.validation.required' })
      .regex(/^-?\d+(\.\d+)?$/, { message: 'common.form.validation.invalid' })
      .refine(
        (val) => {
          const num = parseFloat(val);
          return num >= -180 && num <= 180;
        },
        { message: 'common.form.validation.longitudeMessage' },
      ),
  })
  .superRefine((val, ctx) => {
    const hasExisting = !!val.existingImageSrc;
    const hasNewFile = !!val.image && typeof val.image === 'object';
    if (!hasExisting && !hasNewFile) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['image'],
        message: 'common.form.validation.required',
      });
    }
  });

const ManageLocationForm = ({ locationId }: { locationId: string }) => {
  const { locationDetail } = useLocationDetail(locationId);
  const { mutateAsync } = useUpdateLocationDetail();
  const { t } = useDynamicLocale();

  const form = useForm({
    schema: formSchema,
    mode: 'onBlur',
    defaultValues: {
      title: '',
      subtitle: '',
      mapLink: '',
      existingImageSrc: '',
      image: '',
    },
  });

  useEffect(() => {
    if (locationDetail && Object.keys(locationDetail).length > 0) {
      form.reset({
        title: locationDetail.locationName,
        subtitle: locationDetail.locationSubtitle,
        longitude: locationDetail.longitude,
        latitude: locationDetail.latitude,
        mapLink: locationDetail.locationURL,
        existingImageSrc: locationDetail.locationImage,
        image: '',
      });
    }
  }, [locationDetail]);
  const [imageURL, setImageURL] = useState('');
  useEffect(() => {
    if (locationDetail && Object.keys(locationDetail).length > 0) {
      setImageURL(locationDetail.locationImage);
    }
  }, [locationDetail]);
  return (
    <Form
      form={form}
      onSubmit={async (data) => {
        const formData = form.getValues();
        const imageFile = formData.image?.valueOf();
        let uploadedUrl: string | undefined;
        if (imageFile) {
          uploadedUrl = await uploadFileToS3WithPresignedUrl(imageFile as FileObject);
        }

        const image = imageURL || uploadedUrl || '';
        const payload = {
          locationId: locationDetail.locationId,
          locationName: data.title,
          locationImage: image,
          locationURL: data.mapLink,
          latitude: data.latitude,
          longitude: data.longitude,
          locationSubtitle: data.subtitle,
        };
        await mutateAsync(payload);
      }}
    >
      <div className="w-full space-y-4.5">
        <div className=" gap-4.5 grid grid-cols-2">
          <div>
            <div className="text-control font-bold color-primary flex items-center mb-2">
              <div>{t('configurationManagement.location.page.editModal.locationTitle')}</div>
              <div className="text-destructive-text">*</div>
            </div>
            <Input
              {...form.register('title')}
              maxLength={50}
              placeholder={t('configurationManagement.location.page.editModal.enterLocationTitle')}
            />
          </div>
          <div>
            <div className="text-control font-bold color-primary flex items-center mb-2">
              <div>{t('configurationManagement.location.page.editModal.locationSubtitle')}</div>
              <div className="text-destructive-text">*</div>
            </div>
            <Input
              {...form.register('subtitle')}
              maxLength={50}
              placeholder={t('configurationManagement.location.page.editModal.enterLocationSubtitle')}
            />
          </div>
        </div>
        <div>
          <div className="text-control font-bold color-primary flex items-center mb-2">
            <div>{t('configurationManagement.location.page.editModal.uploadImage')}</div>
            <div className="text-destructive-text">*</div>
          </div>
          {imageURL ? (
            <ImagePreview
              variant="rect"
              className="min-h-0"
              imageClassName="w-full h-[120px]"
              imageSrc={`${imageURL}`}
              alt={''}
              cancelClickHandler={() => {
                setImageURL('');
                form.setValue('existingImageSrc', '', {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
            />
          ) : (
            <Controller
              name="image"
              control={form.control}
              rules={{ required: 'common.form.validation.profileUpload' }}
              render={({ field, fieldState }) => (
                <Upload
                  variant="rect"
                  accept="image/*"
                  multiple={false}
                  buttonText={t('configurationManagement.location.page.editModal.browseFile')}
                  error={fieldState.error?.message}
                  onFilesChange={(files) => {
                    return field.onChange(files?.[0] ?? '');
                  }}
                  previewClassName="object-cover border rounded-sm h-[120px] w-full p-0"
                />
              )}
            />
          )}
          <div className="text-caption font-medium text-muted-foreground mt-3">
            {t('configurationManagement.location.page.editModal.note')}
          </div>
        </div>
        <div>
          <div className="text-control font-bold color-primary flex items-center mb-2">
            <div>{t('configurationManagement.location.page.editModal.mapLink')}</div>
            <div className="text-destructive-text">*</div>
          </div>
          <Input {...form.register('mapLink')} />
        </div>
        <div className="grid grid-cols-2 gap-4.5">
          <div>
            <div className="text-control font-bold color-primary flex items-center mb-2">
              <div>{t('configurationManagement.location.page.editModal.longitude')}</div>
              <div className="text-destructive-text">*</div>
            </div>
            <Input {...form.register('longitude')} />
          </div>
          <div>
            <div className="text-control font-bold color-primary flex items-center mb-2">
              <div>{t('configurationManagement.location.page.editModal.latitude')}</div>
              <div className="text-destructive-text">*</div>
            </div>
            <Input {...form.register('latitude')} />
          </div>
        </div>
      </div>
    </Form>
  );
};
export default ManageLocationForm;
